import React, { useState, useCallback, useEffect, useRef, useMemo, memo, forwardRef } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Slide, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import FileUpload from './FileUpload';
import apiURL from './Config';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return format(date, 'yyyy-MM-dd');
};

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} timeout={20} />
));

const sortByDate = (order) => (a, b) => {
  const dateA = new Date(a.order_date);
  const dateB = new Date(b.order_date);
  return order === 'asc' ? dateA - dateB : dateB - dateA;
};

// Prevent scroll from changing number input values
const preventScrollChange = (e) => e.target.blur();

const TableRow = memo(forwardRef(({ row, index, isInvalid, isHighRate, onInputChange, onDelete }, ref) => {
  return (
    <tr ref={ref} style={{ backgroundColor: isHighRate ? '#fff9e6' : 'transparent' }}>
      <td>{index + 1}</td>
      <td>
        <input
          type="date"
          value={formatDate(row.order_date)}
          onChange={(e) => onInputChange(index, 'order_date', e.target.value)}
        />
      </td>
      <td>
        <input
          type="text"
          value={row.order_id}
          onChange={(e) => onInputChange(index, 'order_id', e.target.value)}
        />
      </td>
      <td>
        <input
          type="text"
          value={row.waybill_no}
          onChange={(e) => onInputChange(index, 'waybill_no', e.target.value)}
        />
      </td>
      <td>
        <input
          type="text"
          value={row.destination_city}
          onChange={(e) => onInputChange(index, 'destination_city', e.target.value)}
        />
      </td>
      <td>
        <input
          type="number"
          min="0"
          value={Math.floor(row.weight)}
          onChange={(e) => onInputChange(index, 'weight', parseInt(e.target.value) || 0)}
          onWheel={preventScrollChange}
        />
      </td>
      <td>
        <div style={{ position: 'relative' }}>
          <input
            type="number"
            min="1"
            value={Math.floor(row.amount)}
            onChange={(e) => onInputChange(index, 'amount', parseInt(e.target.value) || 0)}
            onWheel={preventScrollChange}
            style={{ 
              borderColor: isInvalid ? '#ff4444' : (isHighRate ? '#ff9800' : '#ddd'),
              borderWidth: isHighRate ? '2px' : '1px'
            }}
          />
          {isInvalid && (
            <div style={{
              position: 'absolute',
              bottom: '-18px',
              fontSize: '0.75rem',
              color: '#ff4444'
            }}>
              Need Fix
            </div>
          )}
        </div>
      </td>
      <td>
        <button onClick={() => onDelete(row.waybill_no)}>
          Delete
        </button>
      </td>
    </tr>
  );
}));

const DataTable = () => {
  const [data, setData] = useState([]);
  const [editingData, setEditingData] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openHighRateDialog, setOpenHighRateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const rowRefs = useRef([]);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState([]);
  const [openValidationDialog, setOpenValidationDialog] = useState(false);

  // Virtualization state
  const ROWS_PER_PAGE = 1000;
  const [visibleRows, setVisibleRows] = useState(ROWS_PER_PAGE);
  const tableContainerRef = useRef(null);

  const sortedData = useMemo(() => 
    [...data].sort(sortByDate(sortOrder)),
    [data, sortOrder]
  );

  useEffect(() => {
    setEditingData(sortedData);
    setVisibleRows(ROWS_PER_PAGE); // Reset visible rows when data changes
  }, [sortedData]);

  // Lazy load more rows on scroll
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;
    
    // Trigger when within 400px of the bottom of the page
    if (scrollTop + clientHeight >= scrollHeight - 400) {
      setVisibleRows(prev => Math.min(prev + ROWS_PER_PAGE, editingData.length));
    }
  }, [editingData.length]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Check for high rates (above 500)
  const highRateRows = useMemo(() => 
    editingData
      .map((item, index) => ({
        sNo: index + 1,
        amount: Number(item.amount),
        waybill_no: item.waybill_no,
        weight: item.weight,
        destination: item.destination_city
      }))
      .filter(item => item.amount > 500),
    [editingData]
  );

  const invalidRows = useMemo(() => 
    editingData
      .map((item, index) => ({
        sNo: index + 1,
        amount: Number(item.amount),
        waybill_no: item.waybill_no
      }))
      .filter(item => item.amount <= 0 || isNaN(item.amount)),
    [editingData]
  );

  const handleScrollToRow = useCallback((sNo) => {
    const index = sNo - 1;
    
    // Make sure the row is visible first
    if (index >= visibleRows) {
      setVisibleRows(Math.min(index + ROWS_PER_PAGE, editingData.length));
    }
    
    setTimeout(() => {
      if (rowRefs.current[index]) {
        rowRefs.current[index].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        
        const originalColor = rowRefs.current[index].style.backgroundColor;
        rowRefs.current[index].style.backgroundColor = '#ffeb3b';
        setTimeout(() => {
          rowRefs.current[index].style.backgroundColor = originalColor;
        }, 1500);
      }
    }, 100);
  }, [visibleRows, editingData.length]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Loading data... Please wait');
    try {
      const response = await axios.get(`${apiURL}/waybills`);
      setData(response.data);
      
      // Check for high rates after data is loaded
      const highRates = response.data.filter(item => Number(item.amount) > 500);
      if (highRates.length > 0) {
        setOpenHighRateDialog(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, []);

  // Handle validation errors from FileUpload
  const handleValidationErrors = useCallback((errors) => {
    setValidationErrors(errors);
    setOpenValidationDialog(true);
  }, []);

  const handleDelete = useCallback((waybill_no) => {
    setDeleting(waybill_no);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      await axios.delete(`${apiURL}/waybills/${deleting}`);
      setData(prev => prev.filter(item => item.waybill_no !== deleting));
    } catch (error) {
      console.error('Error deleting record:', error);
    }
    setOpenDeleteDialog(false);
  }, [deleting]);

  const handleInputChange = useCallback((index, field, value) => {
    setEditingData(prev => {
      const newData = [...prev];
      newData[index] = {
        ...newData[index],
        [field]: ['amount', 'weight'].includes(field) 
          ? parseInt(value) || 0 
          : value
      };
      return newData;
    });
  }, []);

  const handleSaveChanges = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Saving changes... Please wait');
    try {
      await axios.put(`${apiURL}/waybills/bulk`, editingData);
      await fetchData();
      alert('Update successful');
    } catch (error) {
      console.error('Update error:', error);
      alert('Error updating data');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [editingData, fetchData]);

  const toggleSortOrder = useCallback(() => {
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  const handleProceedWithHighRates = useCallback(() => {
    setOpenHighRateDialog(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (invalidRows.length > 0) return;
  
    setLoading(true);
    setLoadingMessage('Saving data... Please wait, this may take a moment for large datasets');
    try {
      const response = await axios.post(
        `${apiURL}/api/save-exported-data`, 
        {
          exportData: editingData.map((item, index) => ({
            S_No: index + 1,
            Date: formatDate(item.order_date),
            Order_ID: item.order_id,
            Cannote_No: item.waybill_no,
            Destination: item.destination_city,
            Weight: item.weight,
            RS_PS: item.amount,
          }))
        },
        {
          timeout: 300000,
        }
      );
  
      if (response.data.duplicates && response.data.duplicates.length > 0) {
        alert(`Duplicate Cannote Numbers found:\n${response.data.duplicates.join(', ')}`);
        setLoading(false);
        setLoadingMessage('');
        return;
      }
  
      const { message, totalRecords, batches } = response.data;
      alert(`${message}\nProcessed ${totalRecords} records in ${batches} batches`);
      
      setData([]);
      await fetchData();
    } catch (error) {
      console.error('Save error:', error);
      
      if (error.code === 'ECONNABORTED') {
        alert('Request timeout! Server took too long. Please try again or contact support.');
      } else if (error.response) {
        alert(`Save failed: ${error.response.data.error || 'Server error'}`);
      } else if (error.request) {
        alert('No response from server. Check your connection.');
      } else {
        alert('Save failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [editingData, fetchData, invalidRows.length]);

  // Only render visible rows for performance
  const displayData = useMemo(() => 
    editingData.slice(0, visibleRows),
    [editingData, visibleRows]
  );

  return (
    <div className="datatable">
      <FileUpload onDataFetched={fetchData} onValidationErrors={handleValidationErrors} />
      
      {/* Full-screen loading overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          color: 'white',
        }}>
          <CircularProgress style={{ color: 'white' }} size={50} />
          <p style={{ marginTop: '20px', fontSize: '18px', fontWeight: 'bold' }}>
            {loadingMessage || 'Please wait...'}
          </p>
          <p style={{ fontSize: '14px', opacity: 0.8 }}>
            Do not close or refresh this page
          </p>
        </div>
      )}

      {!loading && (
        <div className="table-container" ref={tableContainerRef}>
          {editingData.length === 0 ? (
            <p className='error'>No data available</p>
          ) : (
            <>
              <p style={{ padding: '5px 10px', fontSize: '14px', color: '#666', margin: '5px 0' }}>
                Showing {Math.min(visibleRows, editingData.length)} of {editingData.length} records
                {visibleRows < editingData.length && ' — scroll down to load more'}
              </p>
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th onClick={toggleSortOrder} style={{ cursor: 'pointer' }}>
                      Date {sortOrder === 'asc' ? '▲' : '▼'}
                    </th>
                    <th>Order_ID</th>
                    <th>Cannote_No</th>
                    <th>Destination</th>
                    <th>Weight</th>
                    <th>RS/PS</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.map((row, index) => {
                    const isInvalid = invalidRows.some(inv => inv.waybill_no === row.waybill_no);
                    const isHighRate = highRateRows.some(hr => hr.waybill_no === row.waybill_no);
                    return (
                      <TableRow
                        key={row.waybill_no}
                        ref={el => rowRefs.current[index] = el}
                        row={row}
                        index={index}
                        isInvalid={isInvalid}
                        isHighRate={isHighRate}
                        onInputChange={handleInputChange}
                        onDelete={handleDelete}
                      />
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={handleSaveChanges}
          disabled={loading || editingData.length === 0}
          style={{
            background: '#2196F3',
            color: 'white',
            padding: '10px 20px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          Save Changes
        </button>
        <button
          onClick={handleSave}
          disabled={invalidRows.length > 0 || loading}
          style={{
            background: invalidRows.length ? '#ffcccc' : '#4CAF50',
            color: invalidRows.length ? '#666' : 'white',
            padding: '10px 20px',
            cursor: invalidRows.length ? 'not-allowed' : 'pointer'
          }}
        >
          {invalidRows.length ? 'Fix Errors to Save' : 'Save Data'}
        </button>
      </div>

      {invalidRows.length > 0 && (
        <div style={{
          marginTop: '10px',
          padding: '10px',
          background: '#fff3f3',
          borderRadius: '4px',
          color: '#ff4444',
          display: 'flex',
          gap: '5px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <span>⚠️ Issues in rows:</span>
          {invalidRows.map((row, idx) => (
            <span 
              key={row.sNo}
              style={{ 
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: '2px 5px',
                borderRadius: '3px'
              }}
              onClick={() => handleScrollToRow(row.sNo)}
            >
              {row.sNo}
              {idx !== invalidRows.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>
      )}

      {/* Validation Errors Dialog - Shows problems from uploaded files */}
      <Dialog 
        open={openValidationDialog} 
        onClose={() => setOpenValidationDialog(false)} 
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ background: '#f44336', color: 'white' }}>
          ⚠️ Data Issues Found in Uploaded File
        </DialogTitle>
        <DialogContent style={{ marginTop: '20px' }}>
          <Alert severity="warning" style={{ marginBottom: '15px' }}>
            Some rows in your file have missing or invalid data. These rows were <strong>NOT imported</strong>. 
            Please review the issues below.
          </Alert>
          
          {validationErrors.map((fileError, fileIdx) => (
            <div key={fileIdx} style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '10px 0', color: '#333' }}>
                📄 File: {fileError.fileName}
                {fileError.validCount > 0 && (
                  <span style={{ color: '#4CAF50', fontWeight: 'normal', fontSize: '14px', marginLeft: '10px' }}>
                    ({fileError.validCount} rows imported successfully)
                  </span>
                )}
              </h4>
              <p style={{ color: '#666', fontSize: '14px', margin: '5px 0' }}>
                {fileError.message}
              </p>
              
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 1 }}>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '8px', textAlign: 'left', width: '80px' }}>File Row</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Problem Field</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Current Value</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileError.errors.map((errRow, errIdx) => (
                      errRow.errors.map((err, eIdx) => (
                        <tr key={`${errIdx}-${eIdx}`} style={{ borderBottom: '1px solid #eee' }}>
                          {eIdx === 0 && (
                            <td style={{ padding: '8px', fontWeight: 'bold', color: '#f44336' }} rowSpan={errRow.errors.length}>
                              Row {errRow.fileRowNumber}
                            </td>
                          )}
                          <td style={{ padding: '8px', fontWeight: 'bold' }}>{err.field}</td>
                          <td style={{ padding: '8px', color: '#999', fontStyle: 'italic' }}>{err.rawValue}</td>
                          <td style={{ padding: '8px', color: '#f44336' }}>{err.message}</td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenValidationDialog(false)} 
            color="primary" 
            variant="contained"
          >
            OK, I'll Fix & Re-upload
          </Button>
          <Button 
            onClick={() => setOpenValidationDialog(false)} 
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* High Rate Warning Dialog */}
      <Dialog 
        open={openHighRateDialog} 
        onClose={() => setOpenHighRateDialog(false)} 
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ background: '#ff9800', color: 'white' }}>
          ⚠️ High Rate Warning
        </DialogTitle>
        <DialogContent style={{ marginTop: '20px' }}>
          <Alert severity="warning" style={{ marginBottom: '15px' }}>
            Found {highRateRows.length} records with rates above ₹500. Please review and confirm.
          </Alert>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>S.No</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Destination</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Weight (g)</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Amount (₹)</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {highRateRows.map((row) => (
                  <tr key={row.waybill_no} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>{row.sNo}</td>
                    <td style={{ padding: '10px' }}>{row.destination}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.weight}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#ff9800' }}>
                      ₹{row.amount}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => {
                          handleScrollToRow(row.sNo);
                          setOpenHighRateDialog(false);
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProceedWithHighRates} color="primary" variant="contained">
            Proceed Anyway
          </Button>
          <Button onClick={() => setOpenHighRateDialog(false)} variant="outlined">
            Review & Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} TransitionComponent={Transition}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DataTable;
